/**
 * Button primitive (PHASE1 §7.4). Variants (primary/secondary/danger/ghost) and
 * sizes; forwards native button props and a ref (React 19 ref-as-prop).
 */
import type { ButtonHTMLAttributes, Ref } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  ref?: Ref<HTMLButtonElement>;
}

// Flat buttons (DESIGN.md §3.6): primary is a solid sky block; secondary and
// danger are text-only and transparent; no borders, shadows, or rings anywhere.
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-sky-600 text-white hover:bg-sky-700 dark:bg-sky-500 dark:hover:bg-sky-400',
  secondary:
    'bg-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100',
  danger:
    'bg-transparent text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/50',
  ghost:
    'bg-transparent text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-[0.8125rem]',
  md: 'px-4 py-2 text-sm',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  type = 'button',
  ref,
  ...props
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      className={`inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors duration-150 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:outline-sky-500 ${VARIANT[variant]} ${SIZE[size]} ${className ?? ''}`}
      {...props}
    />
  );
}
